import { useEffect, useState } from 'react';
import { startOfDay, endOfDay, parseISO, formatISO, isValid, isAfter } from 'date-fns';
import { useHistory, useLocation } from 'react-router';
import { stringify } from 'query-string';

import storage from '@dashboard/services/storage/storage';
import {
  dateRanges,
  dateRangeUnits,
  getUnitsForDateRange,
  getDefaultUnitForDateRange,
} from '@dashboard/scenes/Dashboard/date-ranges';

export function getTimezoneOffset() {
  const offsetMinutesTotal = new Date().getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutesTotal) / 60);
  const offsetMinutes = Math.abs(offsetMinutesTotal) % 60;

  return (
    (offsetMinutesTotal > 0 ? '-' : '+') +
    String(offsetHours).padStart(2, '0') +
    ':' +
    String(offsetMinutes).padStart(2, '0')
  );
}

/**
 * The date range and unit a statistics screen shows, kept in the URL and remembered under
 * `storageKey`. `allTimeStart` is where All time begins; `billingPeriod` ({ start, end }) backs
 * the Current billing period option.
 */
export default function useStatisticsRange({
  storageKey,
  defaultRange = 'monthToDate',
  allTimeStart,
  billingPeriod,
  // Leaves the address bare while the default range and daily unit are showing.
  bareDefaultUrl = false,
}) {
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [range, setRange] = useState(
    query.get('range') || storage.local.getItem(`${storageKey}-range`) || defaultRange,
  );
  const [unit, setUnit] = useState(
    query.get('unit') || storage.local.getItem(`${storageKey}-unit`) || dateRangeUnits.DAY,
  );
  const [customStartDate, setCustomStartDate] = useState(
    query.get('startDate') ? parseISO(query.get('startDate')) : null,
  );
  const [customEndDate, setCustomEndDate] = useState(
    query.get('endDate') ? parseISO(query.get('endDate')) : null,
  );

  useEffect(() => {
    storage.local.setItem(`${storageKey}-range`, range);
  }, [storageKey, range]);

  useEffect(() => {
    storage.local.setItem(`${storageKey}-unit`, unit);
  }, [storageKey, unit]);

  useEffect(() => {
    const params = {
      range,
      unit,
      ...(range === 'custom' && customStartDate
        ? { startDate: formatISO(customStartDate, { representation: 'date' }) }
        : {}),
      ...(range === 'custom' && customEndDate
        ? { endDate: formatISO(customEndDate, { representation: 'date' }) }
        : {}),
    };

    const isDefault = range === defaultRange && unit === dateRangeUnits.DAY;
    const search = bareDefaultUrl && isDefault ? '' : stringify(params);
    if (search !== history.location.search.replace(/^\?/, '')) history.replace({ search });
  }, [range, unit, customStartDate, customEndDate, history, bareDefaultUrl, defaultRange]);

  useEffect(() => {
    if (range === 'custom' && customStartDate && customEndDate) {
      const availableUnits = getUnitsForDateRange(
        startOfDay(customStartDate),
        endOfDay(customEndDate),
      );
      if (!availableUnits.includes(unit)) {
        setUnit(getDefaultUnitForDateRange(startOfDay(customStartDate), endOfDay(customEndDate)));
      }
    }
  }, [range, unit, customStartDate, customEndDate, setUnit]);

  const hasBillingPeriod = !!(billingPeriod?.start && billingPeriod?.end);

  let dateStart;
  let dateEnd;

  if (range === 'custom') {
    dateStart = customStartDate ? startOfDay(customStartDate) : startOfDay(new Date());
    dateEnd = customEndDate ? endOfDay(customEndDate) : endOfDay(new Date());
  } else if (range === 'allTime' && allTimeStart) {
    dateStart = allTimeStart;
    dateEnd = dateRanges.allTime.end;
  } else if (range === 'currentBillingPeriod' && hasBillingPeriod) {
    dateStart = startOfDay(new Date(billingPeriod.start));
    dateEnd = endOfDay(new Date(billingPeriod.end));
  } else {
    dateStart = dateRanges[range]?.start || startOfDay(new Date());
    dateEnd = dateRanges[range]?.end || endOfDay(new Date());
  }

  if (!isValid(dateStart)) dateStart = dateRanges.monthToDate.start;
  if (!isValid(dateEnd)) dateEnd = dateRanges.monthToDate.end;
  if (!isAfter(dateEnd, dateStart)) dateEnd = dateStart;

  const availableUnits =
    range === 'custom' ? getUnitsForDateRange(dateStart, dateEnd) : dateRanges[range]?.units || [];

  function handleSetRange(nextRange) {
    setRange(nextRange);

    if (nextRange === 'custom') {
      const customUnits = getUnitsForDateRange(dateStart, dateEnd);
      if (!customUnits.includes(unit)) setUnit(getDefaultUnitForDateRange(dateStart, dateEnd));
      return;
    }

    setCustomStartDate(null);
    setCustomEndDate(null);

    if (nextRange === 'currentBillingPeriod' && hasBillingPeriod) {
      const start = startOfDay(new Date(billingPeriod.start));
      const end = endOfDay(new Date(billingPeriod.end));
      if (!getUnitsForDateRange(start, end).includes(unit)) {
        setUnit(getDefaultUnitForDateRange(start, end));
      }
    } else {
      const dateRange = dateRanges[nextRange];
      if (dateRange && !dateRange.units.includes(unit)) setUnit(dateRange.defaultUnit);
    }
  }

  return {
    range,
    unit,
    setRange: handleSetRange,
    setUnit,
    customStartDate,
    customEndDate,
    setCustomStartDate,
    setCustomEndDate,
    dateStart,
    dateEnd,
    availableUnits,
    timezoneOffset: getTimezoneOffset(),
  };
}
